import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Ban, MessagesSquare } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { extractErrorMessage } from '@/shared/api/errors';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { useSupportConversations } from '@/entities/support-chat';
import { formatDateTime } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';

const PAGE_SIZE = 20;

export const WidgetsSupportChatConversationsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = useSupportConversations(params);
  const rows = query.data?.results ?? [];
  const total = query.data?.count ?? 0;

  return (
    <>
      <PageHeader title={t('support.list.title')} subtitle={t('support.list.subtitle')} />

      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('support.list.searchPlaceholder')}
        />

        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('support.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<MessagesSquare className="h-8 w-8" />}
            title={t('support.list.emptyTitle')}
            description={t('support.list.emptyDescription')}
          />
        ) : (
          <>
            <ul className="space-y-2">
              {rows.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/support/${c.user_id}`)}
                    className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left shadow-card transition-colors hover:bg-slate-50"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-ink-muted">
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <MessagesSquare className="h-5 w-5" aria-hidden />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate font-semibold text-ink">
                          {c.full_name || c.email}
                        </p>
                        {c.is_blocked && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                            <Ban className="h-3 w-3" aria-hidden />
                            {t('support.list.blocked')}
                          </span>
                        )}
                      </div>
                      <p className="truncate text-xs text-ink-muted">{c.email}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-xs text-ink-muted">
                        {c.last_message_at ? formatDateTime(c.last_message_at) : '—'}
                      </span>
                      {c.unread_count > 0 && (
                        <span
                          className={cn(
                            'inline-flex min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 py-0.5 text-xs font-semibold text-white',
                          )}
                        >
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          </>
        )}
      </div>
    </>
  );
};
